import { useEffect, useState } from 'react';
import { MenuItem, Select, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { CheckIcon, ChevronRightIcon, GridIcon, SpeakerViewIcon } from 'assets';
import { useTranslation } from 'react-i18next';
import { LayoutType } from 'types';
import { useAppSettings, useParticipants, useWhiteboard } from 'hooks';
import { GRID_VIEW_THRESHOLD } from '../../../constants';
import { IWhiteboardContext } from 'context';

import styles from './LayoutList.module.scss';

type LayoutListProps = {
  forceLightTheme?: boolean;
};

export function LayoutList({ forceLightTheme }: LayoutListProps) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const { layoutType, setLayoutType } = useAppSettings();
  const participants = useParticipants();
  const { state }: IWhiteboardContext = useWhiteboard();

  const [isMultipleParticipantsViewAllowed, setIsMultipleParticipantsViewAllowed] = useState(participants.length >= 1);

  useEffect(() => {
    if (participants.length >= 1 && state === 'off') {
      setIsMultipleParticipantsViewAllowed(true);
    } else {
      setIsMultipleParticipantsViewAllowed(false);
    }
  }, [participants.length, state]);

  return (
    <>
      <Select
        variant='outlined'
        IconComponent={ChevronRightIcon}
        value={layoutType}
        onChange={(e) => setLayoutType(e.target.value as LayoutType)}
        className={styles.layoutSelect}
        classes={{
          icon: styles.chevron,
        }}
        MenuProps={
          forceLightTheme
            ? {
                PaperProps: {
                  sx: {
                    backgroundColor: 'var(--app-field-surface)',
                    color: 'var(--app-text-default)',
                  },
                },
                MenuListProps: {
                  sx: {
                    '.MuiMenuItem-root': {
                      color: 'var(--app-text-default)',
                    },
                    '.MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'var(--app-field-surface-disabled)',
                      color: 'var(--app-text-default)',
                    },
                    '.MuiMenuItem-root.Mui-selected:hover': {
                      backgroundColor: 'var(--app-field-surface-disabled)',
                      color: 'var(--app-text-default)',
                    },
                    '.MuiMenuItem-root .MuiTypography-root': {
                      color: 'var(--app-text-default)',
                    },
                  },
                },
              }
            : undefined
        }
        disabled={!isMultipleParticipantsViewAllowed}
        sx={
          forceLightTheme
            ? {
                backgroundColor: 'var(--app-field-surface)',
                color: 'var(--app-text-default)',
                '.MuiSelect-icon': {
                  color: 'var(--app-text-muted)',
                },
                '.MuiSelect-select': {
                  backgroundColor: 'var(--app-field-surface)',
                  color: 'var(--app-text-default)',
                },
                '.MuiOutlinedInput-root': {
                  backgroundColor: 'var(--app-field-surface)',
                },
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--app-field-divider)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--app-field-divider)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--app-field-divider)',
                },
              }
            : undefined
        }
      >
        <MenuItem value={LayoutType.GRID}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{
              width: '100%',
              direction: 'ltr',
            }}
          >
            {isRtl && layoutType === LayoutType.GRID && (
              <CheckIcon
                className='check-icon'
                sx={{
                  fontSize: '0.9rem',
                  color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                }}
              />
            )}
            <Stack
              direction='row'
              alignItems='center'
              justifyContent={isRtl ? 'flex-end' : 'flex-start'}
              sx={{ gap: '8px', flex: 1, minWidth: 0 }}
            >
              {isRtl ? (
                <>
                  <Typography
                    component='p'
                    variant={isDesktop ? 'body5' : 'body2'}
                    textTransform='capitalize'
                    sx={{
                      color: forceLightTheme ? 'var(--app-text-default)' : undefined,
                      textAlign: 'right',
                    }}
                  >
                    {t('layoutGrid')}
                  </Typography>
                  <GridIcon
                    color='primary'
                    sx={{
                      fontSize: '1.5rem',
                      color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                    }}
                  />
                </>
              ) : (
                <>
                  <GridIcon
                    color='primary'
                    sx={{
                      fontSize: '1.5rem',
                      color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                    }}
                  />
                  <Typography
                    component='p'
                    variant={isDesktop ? 'body5' : 'body2'}
                    textTransform='capitalize'
                    sx={{
                      color: forceLightTheme ? 'var(--app-text-default)' : undefined,
                      textAlign: 'left',
                    }}
                  >
                    {t('layoutGrid')}
                  </Typography>
                </>
              )}
            </Stack>
            {!isRtl && layoutType === LayoutType.GRID && (
              <CheckIcon
                className='check-icon'
                sx={{
                  fontSize: '0.9rem',
                  color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                }}
              />
            )}
          </Stack>
        </MenuItem>
        <MenuItem value={LayoutType.SPEAKER}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{
              width: '100%',
              direction: 'ltr',
            }}
          >
            {isRtl && layoutType === LayoutType.SPEAKER && (
              <CheckIcon
                className='check-icon'
                sx={{
                  fontSize: '0.9rem',
                  color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                }}
              />
            )}
            <Stack
              direction='row'
              alignItems='center'
              justifyContent={isRtl ? 'flex-end' : 'flex-start'}
              sx={{ gap: '8px', flex: 1, minWidth: 0 }}
            >
              {isRtl ? (
                <>
                  <Typography
                    component='p'
                    variant={isDesktop ? 'body5' : 'body2'}
                    textTransform='capitalize'
                    sx={{
                      color: forceLightTheme ? 'var(--app-text-default)' : undefined,
                      textAlign: 'right',
                    }}
                  >
                    {t('layoutSpeaker')}
                  </Typography>
                  <SpeakerViewIcon
                    color='primary'
                    sx={{
                      fontSize: '1.5rem',
                      color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                    }}
                  />
                </>
              ) : (
                <>
                  <SpeakerViewIcon
                    color='primary'
                    sx={{
                      fontSize: '1.5rem',
                      color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                    }}
                  />
                  <Typography
                    component='p'
                    variant={isDesktop ? 'body5' : 'body2'}
                    textTransform='capitalize'
                    sx={{
                      color: forceLightTheme ? 'var(--app-text-default)' : undefined,
                      textAlign: 'left',
                    }}
                  >
                    {t('layoutSpeaker')}
                  </Typography>
                </>
              )}
            </Stack>
            {!isRtl && layoutType === LayoutType.SPEAKER && (
              <CheckIcon
                className='check-icon'
                sx={{
                  fontSize: '0.9rem',
                  color: forceLightTheme ? 'var(--app-brand-primary)' : undefined,
                }}
              />
            )}
          </Stack>
        </MenuItem>
      </Select>
    </>
  );
}
